import Avatar from "@mui/joy/Avatar";
import { Link } from "@mui/material";
import AutoScroll from "embla-carousel-auto-scroll";
import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useState } from "react";
import logoBlack from "@images/logos/logoBlack.png";
import GeneralStrings from "@strings/GeneralStrings.json";
import SpotifyService from "@services/SpotifyService";
import "./ArtistsBlade.scss";

interface Artist {
  name: string;
  external_urls: { spotify: string };
  images: { url: string }[];
}

let artistsCache: Promise<Artist[]> | null = null;

const fetchArtistsOnce = (): Promise<Artist[]> => {
  if (!artistsCache) {
    artistsCache = (async () => {
      const { data } = await SpotifyService.getArtists();
      return data.artists;
    })();
    artistsCache.catch(() => {
      artistsCache = null;
    });
  }
  return artistsCache;
};

/** Test-only: clears the module-level artists cache so each test starts fresh. */
export const __resetArtistsCache = () => {
  artistsCache = null;
};

const ArtistsBlade = () => {
  const [artists, setArtists] = useState<Artist[]>([]);
  const artistsStrings = GeneralStrings.artists;

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, dragFree: true },
    [AutoScroll({ playOnInit: true, speed: 1, stopOnInteraction: false })],
  );

  const handleMouseEnter = useCallback(() => {
    (emblaApi?.plugins() as any)?.autoScroll?.stop();
  }, [emblaApi]);

  const handleMouseLeave = useCallback(() => {
    (emblaApi?.plugins() as any)?.autoScroll?.play();
  }, [emblaApi]);

  useEffect(() => {
    fetchArtistsOnce()
      .then(setArtists)
      .catch(() => {});
  }, []);

  const slides =
    artists.length > 0 ? [...artists, ...artists, ...artists, ...artists] : [];

  return (
    <section className="section artists" id="artists">
      <div className="container-fluid">
        <div className="title-area">
          <h2>
            <em>Artists</em> & Collabs
          </h2>
          <div className="separator">
            <img src={logoBlack} className="seperator-img" alt="logo" />
          </div>
          <p className="artist-description">{artistsStrings.description}</p>
        </div>

        <div
          className="embla"
          ref={emblaRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="embla__container">
            {slides.map((artist, index) => (
              <div
                className="embla__slide"
                key={"artist-" + index + "-" + artist?.name}
              >
                <Link
                  href={artist?.external_urls?.spotify}
                  underline="none"
                  className="flex flex-col"
                  target="_blank"
                >
                  <Avatar
                    className="artist-img"
                    color="neutral"
                    size="lg"
                    src={artist?.images[0]?.url}
                    variant="soft"
                  />
                  <span className="artist-name text-center">
                    {artist?.name}
                  </span>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ArtistsBlade;
